import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft, ArrowRight, Menu, X, BookOpen, User, ChevronRight } from 'lucide-react';
import { STATIC_BLOG_CATEGORIES, STATIC_BLOG_POSTS, getStaticPostBySlug } from '../data/staticBlogPosts';

const API_URL = import.meta.env.VITE_API_URL;
const SITE_URL = 'https://integrativehealthjournal.com';
const BOOK_URL = 'https://www.amazon.com/Macro-Masterclass-Scientific-Approach-calculator/dp/B0G6CQT465';

const getPostImage = (post) => post.featured_image || `${API_URL}/blog/image/${post.slug}`;

const mergePosts = (apiPosts = [], staticPosts = []) => {
  const seen = new Set();
  return [...staticPosts, ...apiPosts].filter((post) => {
    if (!post?.slug || seen.has(post.slug)) return false;
    seen.add(post.slug);
    return true;
  });
};

const mergeCategories = (apiCategories = [], staticCategories = []) => {
  const bySlug = new Map();
  [...apiCategories, ...staticCategories].forEach((cat) => {
    if (!cat?.category_slug) return;
    const existing = bySlug.get(cat.category_slug);
    bySlug.set(cat.category_slug, {
      ...cat,
      count: (existing?.count || 0) + (cat.count || 0),
    });
  });
  return Array.from(bySlug.values());
};

const decodeEntities = (str) => {
  if (!str) return '';
  return str
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code)))
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatDateISO = (d) => {
  if (!d) return '';
  return new Date(d).toISOString();
};

// Inject a <script type="application/ld+json"> tag into <head>
const injectSchema = (id, obj) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
};

const removeSchema = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

// Set or update a <meta> tag
const setMeta = (name, content, attr = 'name') => {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

// Set or update <link rel="canonical">
const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [recent, setRecent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const staticPost = getStaticPostBySlug(slug);
    Promise.all([
      staticPost
        ? Promise.resolve(staticPost)
        : fetch(`${API_URL}/blog/slug/${slug}`).then(r => r.json()).catch(() => ({ error: 'Not found' })),
      fetch(`${API_URL}/blog/recent?limit=6`).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/blog/categories`).then(r => r.json()).catch(() => []),
    ]).then(([postData, recentData, catsData]) => {
      const resolvedPost = postData?.error ? staticPost : postData;
      if (!resolvedPost) { navigate('/404', { replace: true }); return; }
      setPost(resolvedPost);
      setRecent(mergePosts(recentData, STATIC_BLOG_POSTS.filter((item) => item.slug !== slug)).slice(0, 6));
      setCategories(mergeCategories(catsData, STATIC_BLOG_CATEGORIES));

      const title = decodeEntities(resolvedPost.seo_title || resolvedPost.title);
      const description = resolvedPost.seo_description || resolvedPost.excerpt || '';
      const canonicalUrl = `${SITE_URL}/${resolvedPost.slug}`;
      const author = resolvedPost.author || 'Patrick Matthews';
      const imageUrl = resolvedPost.featured_image || `${API_URL}/blog/image/${resolvedPost.slug}`;

      // Page title + meta
      document.title = title;
      setMeta('description', description);
      setCanonical(canonicalUrl);

      // Open Graph
      setMeta('og:type', 'article', 'property');
      setMeta('og:title', title, 'property');
      setMeta('og:description', description, 'property');
      setMeta('og:url', canonicalUrl, 'property');
      setMeta('og:image', imageUrl, 'property');
      setMeta('og:site_name', 'Integrative Health Journal', 'property');

      // Twitter Card
      setMeta('twitter:card', 'summary_large_image');
      setMeta('twitter:title', title);
      setMeta('twitter:description', description);
      setMeta('twitter:image', imageUrl);

      // Article schema
      injectSchema('schema-article', {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description,
        image: imageUrl,
        url: canonicalUrl,
        datePublished: formatDateISO(resolvedPost.published_at),
        dateModified: formatDateISO(resolvedPost.modified_at || resolvedPost.published_at),
        author: {
          '@type': 'Person',
          name: author,
          url: `${SITE_URL}/blog`,
          sameAs: [BOOK_URL],
        },
        publisher: {
          '@type': 'Organization',
          name: 'Integrative Health Journal',
          url: SITE_URL,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/images/ihj-facebook-logo.jpg` },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
      });

      // Breadcrumb schema
      const breadcrumbItems = [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      ];
      if (resolvedPost.category) {
        breadcrumbItems.push({
          '@type': 'ListItem', position: 3,
          name: resolvedPost.category,
          item: `${SITE_URL}/blog/category/${resolvedPost.category_slug}`,
        });
        breadcrumbItems.push({ '@type': 'ListItem', position: 4, name: decodeEntities(resolvedPost.title), item: canonicalUrl });
      } else {
        breadcrumbItems.push({ '@type': 'ListItem', position: 3, name: decodeEntities(resolvedPost.title), item: canonicalUrl });
      }
      injectSchema('schema-breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems,
      });

      // FAQ schema (if present)
      if (resolvedPost.faq_json) {
        try {
          const faqs = JSON.parse(resolvedPost.faq_json);
          if (Array.isArray(faqs) && faqs.length > 0) {
            injectSchema('schema-faq', {
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            });
          }
        } catch (_) {}
      } else {
        removeSchema('schema-faq');
      }
    }).catch(() => {
      const fallbackPost = getStaticPostBySlug(slug);
      if (fallbackPost) {
        setPost(fallbackPost);
        setRecent(STATIC_BLOG_POSTS.filter((item) => item.slug !== slug).slice(0, 6));
        setCategories(STATIC_BLOG_CATEGORIES);
        return;
      }
      navigate('/404', { replace: true });
    })
      .finally(() => setLoading(false));

    // Cleanup schemas when navigating away
    return () => {
      removeSchema('schema-article');
      removeSchema('schema-breadcrumb');
      removeSchema('schema-faq');
    };
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600" />
    </div>
  );

  if (!post) return null;

  const cats = (() => { try { return JSON.parse(post.categories_json || '[]'); } catch { return []; } })();
  const author = post.author || 'Patrick Matthews';

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 dark:bg-gray-950/90 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/images/ihj-facebook-logo.jpg" alt="Integrative Health Journal" className="h-14 w-14 rounded-full object-cover" />
            <span className="font-bold text-base">Integrative Health Journal</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
            <Link to="/blog" className="hover:text-gray-900 dark:hover:text-white">Blog</Link>
            {categories.slice(0, 4).map(c => (
              <Link key={c.category_slug} to={`/blog/category/${c.category_slug}`}
                className="hover:text-gray-900 dark:hover:text-white capitalize">{c.category}</Link>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900">Sign In</Link>
            <Link to="/login?tab=signup" className="px-4 py-1.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold rounded-lg">
              Get Started Free
            </Link>
          </div>
          <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 px-4 py-3 space-y-2">
            <Link to="/blog" className="block py-2 text-sm text-gray-700 dark:text-gray-300">All Posts</Link>
            {categories.slice(0, 5).map(c => (
              <Link key={c.category_slug} to={`/blog/category/${c.category_slug}`}
                className="block py-2 text-sm text-gray-700 dark:text-gray-300 capitalize">{c.category}</Link>
            ))}
            <Link to="/login?tab=signup" className="block py-2 text-sm font-bold text-indigo-600">Open NutriTrack</Link>
          </div>
        )}
      </nav>

      {/* Main layout */}
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid lg:grid-cols-[1fr_300px] gap-12">

          {/* Article */}
          <article itemScope itemType="https://schema.org/Article">

            {/* Breadcrumb nav */}
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-gray-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-indigo-600 transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3 flex-shrink-0" />
              <Link to="/blog" className="hover:text-indigo-600 transition-colors">Blog</Link>
              {post.category && (
                <>
                  <ChevronRight className="w-3 h-3 flex-shrink-0" />
                  <Link to={`/blog/category/${post.category_slug}`} className="hover:text-indigo-600 transition-colors capitalize">{post.category}</Link>
                </>
              )}
              <ChevronRight className="w-3 h-3 flex-shrink-0" />
              <span className="text-gray-400 line-clamp-1">{decodeEntities(post.title)}</span>
            </nav>

            {/* Category badge */}
            {post.category && (
              <Link to={`/blog/category/${post.category_slug}`}
                className="inline-block bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-semibold px-3 py-1 rounded-full mb-4 capitalize">
                {post.category}
              </Link>
            )}

            {/* Title */}
            <h1 itemProp="headline" className="text-3xl sm:text-4xl font-black leading-tight mb-4">
              {decodeEntities(post.title)}
            </h1>

            {/* Meta row */}
            <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400 mb-8 pb-6 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                <span itemProp="author" itemScope itemType="https://schema.org/Person">
                  <span itemProp="name">{author}</span>
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <time itemProp="datePublished" dateTime={formatDateISO(post.published_at)}>
                  {formatDate(post.published_at)}
                </time>
              </div>
              {post.modified_at && post.modified_at !== post.published_at && (
                <span className="text-xs">Updated <time itemProp="dateModified" dateTime={formatDateISO(post.modified_at)}>{formatDate(post.modified_at)}</time></span>
              )}
            </div>

            {/* Featured image */}
            <img
              src={getPostImage(post)}
              alt={decodeEntities(post.title)}
              itemProp="image"
              className="w-full rounded-xl mb-8 object-cover max-h-96"
              loading="lazy"
            />

            {/* Article content */}
            <div
              itemProp="articleBody"
              className="
                blog-content prose prose-lg prose-gray dark:prose-invert max-w-none
                prose-headings:font-bold prose-headings:tracking-tight
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                prose-img:rounded-xl prose-img:shadow-md prose-img:mx-auto
                prose-table:w-full prose-table:border-collapse prose-table:text-sm
                prose-th:bg-indigo-50 dark:prose-th:bg-indigo-900/30 prose-th:px-4 prose-th:py-2.5 prose-th:text-left prose-th:font-semibold prose-th:border prose-th:border-gray-200 dark:prose-th:border-gray-700
                prose-td:px-4 prose-td:py-2.5 prose-td:border prose-td:border-gray-200 dark:prose-td:border-gray-700
                prose-blockquote:border-l-4 prose-blockquote:border-indigo-500 prose-blockquote:bg-indigo-50 dark:prose-blockquote:bg-indigo-900/20 prose-blockquote:py-2 prose-blockquote:px-5 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
                prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                [&_.article-summary]:bg-gradient-to-r [&_.article-summary]:from-blue-50 [&_.article-summary]:to-indigo-50 dark:[&_.article-summary]:from-blue-900/20 dark:[&_.article-summary]:to-indigo-900/20 [&_.article-summary]:border [&_.article-summary]:border-blue-200 dark:[&_.article-summary]:border-blue-800 [&_.article-summary]:rounded-xl [&_.article-summary]:p-5 [&_.article-summary]:mb-8 [&_.article-summary]:text-sm [&_.article-summary]:leading-relaxed [&_.article-summary]:text-gray-700 dark:[&_.article-summary]:text-gray-300 [&_.article-summary]:not-prose
                [&_.faq-section]:not-prose [&_.faq-section]:mt-10
                [&_.faq-item]:border-b [&_.faq-item]:border-gray-100 dark:[&_.faq-item]:border-gray-800 [&_.faq-item]:py-4
                [&_.faq-item_h3]:text-base [&_.faq-item_h3]:font-semibold [&_.faq-item_h3]:text-gray-900 dark:[&_.faq-item_h3]:text-white [&_.faq-item_h3]:mb-2 [&_.faq-item_h3]:mt-0
                [&_.faq-item_p]:text-sm [&_.faq-item_p]:text-gray-600 dark:[&_.faq-item_p]:text-gray-400 [&_.faq-item_p]:leading-relaxed [&_.faq-item_p]:mb-0
                [&_.app-cta]:not-prose [&_.app-cta]:bg-gradient-to-r [&_.app-cta]:from-emerald-50 [&_.app-cta]:to-teal-50 dark:[&_.app-cta]:from-emerald-900/20 dark:[&_.app-cta]:to-teal-900/20 [&_.app-cta]:border [&_.app-cta]:border-emerald-200 dark:[&_.app-cta]:border-emerald-800 [&_.app-cta]:rounded-xl [&_.app-cta]:p-5 [&_.app-cta]:my-8 [&_.app-cta]:text-sm [&_.app-cta]:text-gray-700 dark:[&_.app-cta]:text-gray-300
                [&_.author-note]:not-prose [&_.author-note]:bg-gray-50 dark:[&_.author-note]:bg-gray-800/50 [&_.author-note]:border [&_.author-note]:border-gray-200 dark:[&_.author-note]:border-gray-700 [&_.author-note]:rounded-xl [&_.author-note]:p-4 [&_.author-note]:text-sm [&_.author-note]:text-gray-600 dark:[&_.author-note]:text-gray-400 [&_.author-note]:my-6
              "
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            {cats.length > 0 && (
              <div className="mt-10 pt-6 border-t border-gray-100 dark:border-gray-800 flex flex-wrap gap-2">
                {cats.map(s => (
                  <Link key={s} to={`/blog/category/${s}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-gray-700 dark:text-gray-300 hover:text-indigo-700 text-xs font-medium rounded-full transition-colors">
                    <Tag className="w-3 h-3" />{s}
                  </Link>
                ))}
              </div>
            )}

            {/* Author Bio */}
            <div className="mt-10 pt-8 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-start gap-4 bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center flex-shrink-0 text-white font-bold text-lg">
                  {author.charAt(0)}
                </div>
                <div>
                  <div className="font-bold text-base text-gray-900 dark:text-white mb-0.5">{author}</div>
                  <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium mb-2">Founder, Integrative Health Journal</div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                    Patrick Matthews is a nutrition educator and the creator of NutriTrack. He is the author of
                    {' '}<a href={BOOK_URL} target="_blank" rel="noopener noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline">
                      Macro Masterclass: A Scientific Approach to Nutrition
                    </a>{' '}
                    — a comprehensive guide to using macronutrient tracking for sustainable fat loss and muscle building.
                  </p>
                  <a href={BOOK_URL} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-lg text-xs font-semibold hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors">
                    <BookOpen className="w-3.5 h-3.5" /> Get the Book on Amazon
                  </a>
                </div>
              </div>
            </div>

            {/* CTA Banner */}
            <div className="mt-10 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-2xl p-8 text-white text-center">
              <div className="text-2xl font-black mb-2">Track everything you just read about</div>
              <p className="text-blue-200 mb-6 text-sm">IHJ NutriTrack's AI coach searches the web for accurate nutrition info and knows your exact goals and remaining macros.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/login?tab=signup"
                  className="px-6 py-3 bg-white text-indigo-700 font-bold rounded-xl hover:bg-blue-50 transition-all text-sm">
                  Start Free
                </Link>
                <Link to="/login?tab=book"
                  className="px-6 py-3 bg-white/15 hover:bg-white/25 text-white font-semibold rounded-xl text-sm border border-white/20">
                  I Have the Book
                </Link>
              </div>
            </div>

          </article>

          {/* Sidebar */}
          <aside className="space-y-8">

            {/* App promo */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border border-indigo-100 dark:border-indigo-800">
              <img src="/images/ihj-facebook-logo.jpg" alt="Integrative Health Journal" className="h-14 w-14 rounded-full object-cover mb-3" />
              <h3 className="font-bold text-base mb-2">Track Your Nutrition Free</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                AI coach with live web search. Log meals, build grocery lists, scan nutrition labels.
              </p>
              <Link to="/login?tab=signup"
                className="block w-full py-2.5 text-center text-sm font-bold bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 transition-all">
                Get Started Free
              </Link>
            </div>

            {/* Book promo */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 rounded-2xl p-6 border border-amber-200 dark:border-amber-800">
              <BookOpen className="w-8 h-8 text-amber-600 dark:text-amber-400 mb-3" />
              <h3 className="font-bold text-base mb-2">Macro Masterclass</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                The scientific approach to nutrition tracking. Used by thousands to hit their goals.
              </p>
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 text-center text-sm font-bold bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-all">
                <BookOpen className="w-4 h-4" /> Get it on Amazon
              </a>
            </div>

            {/* Recent posts */}
            {recent.length > 0 && (
              <div>
                <h3 className="font-bold text-base mb-4 pb-2 border-b border-gray-100 dark:border-gray-800">More Articles</h3>
                <div className="space-y-4">
                  {recent.filter(r => r.slug !== slug).slice(0, 5).map(r => (
                    <Link key={r.slug} to={`/${r.slug}`} className="flex gap-3 group">
                      <img src={getPostImage(r)} alt={r.title}
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                      <div>
                        <p className="text-sm font-medium leading-snug group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors line-clamp-2">{r.title}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatDate(r.published_at)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Categories */}
            {categories.length > 0 && (
              <div>
                <h3 className="font-bold text-base mb-4 pb-2 border-b border-gray-100 dark:border-gray-800">Categories</h3>
                <div className="space-y-1">
                  {categories.map(cat => (
                    <Link key={cat.category_slug} to={`/blog/category/${cat.category_slug}`}
                      className="flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
                      <span className="text-sm capitalize group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{cat.category}</span>
                      <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">{cat.count}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

          </aside>
        </div>
      </div>

    </div>
  );
}
